// PDF Download utility for e-book exercises
// Uses browser's print-to-PDF functionality with clean formatting

export const downloadExercisePDF = (exerciseData, title, filename) => {
  // Create a new window with styled content for printing
  const printWindow = window.open('', '_blank');
  
  if (!printWindow) {
    alert('Please allow popups to download the PDF');
    return;
  }

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Generate HTML content for the PDF
  const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${title}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: 'Georgia', serif;
      line-height: 1.6;
      color: #1a1a1a;
      padding: 40px;
      max-width: 800px;
      margin: 0 auto;
    }
    
    .header {
      text-align: center;
      margin-bottom: 30px;
      padding-bottom: 20px;
      border-bottom: 2px solid #2d5a3d;
    }
    
    .header h1 {
      font-size: 24px;
      color: #2d5a3d;
      margin-bottom: 8px;
    }
    
    .header .subtitle {
      font-size: 14px;
      color: #666;
    }
    
    .header .date {
      font-size: 12px;
      color: #888;
      margin-top: 8px;
    }
    
    .wisdom-box {
      background: #f8f5e6;
      border-left: 4px solid #c9a227;
      padding: 15px 20px;
      margin: 20px 0;
      font-style: italic;
    }
    
    .wisdom-box .author {
      font-style: normal;
      font-weight: bold;
      color: #666;
      margin-top: 10px;
    }
    
    .exercise-section {
      margin: 30px 0;
    }
    
    .exercise-section h2 {
      font-size: 18px;
      color: #2d5a3d;
      margin-bottom: 15px;
      padding-bottom: 8px;
      border-bottom: 1px solid #ddd;
    }
    
    .exercise-item {
      margin: 20px 0;
      page-break-inside: avoid;
    }
    
    .exercise-number {
      display: inline-block;
      width: 28px;
      height: 28px;
      background: #2d5a3d;
      color: white;
      border-radius: 50%;
      text-align: center;
      line-height: 28px;
      font-weight: bold;
      font-size: 14px;
      margin-right: 10px;
    }
    
    .exercise-context {
      font-weight: bold;
      color: #333;
      margin-bottom: 5px;
    }
    
    .exercise-example {
      font-style: italic;
      color: #666;
      margin-bottom: 15px;
      font-size: 14px;
    }
    
    .response-section {
      margin: 10px 0;
    }
    
    .response-label {
      font-weight: bold;
      color: #555;
      font-size: 13px;
      margin-bottom: 5px;
    }
    
    .response-label.judgment {
      color: #8b4513;
    }
    
    .response-label.feeling {
      color: #c41e3a;
    }
    
    .response-label.need {
      color: #2d5a3d;
    }
    
    .response-value {
      background: #f9f9f9;
      border: 1px solid #ddd;
      border-radius: 4px;
      padding: 10px 12px;
      min-height: 40px;
      margin-bottom: 10px;
    }
    
    .response-value.empty {
      color: #999;
      font-style: italic;
    }
    
    .translation-row {
      display: flex;
      gap: 10px;
      margin-bottom: 10px;
    }
    
    .translation-prefix {
      font-weight: bold;
      color: #555;
      min-width: 80px;
      flex-shrink: 0;
    }
    
    .reflection-section {
      margin-top: 40px;
      padding-top: 20px;
      border-top: 2px solid #2d5a3d;
    }
    
    .reflection-section h2 {
      color: #6b4c9a;
    }
    
    .footer {
      margin-top: 40px;
      padding-top: 20px;
      border-top: 1px solid #ddd;
      text-align: center;
      font-size: 12px;
      color: #888;
    }
    
    .footer .brand {
      color: #2d5a3d;
      font-weight: bold;
    }
    
    @media print {
      body {
        padding: 20px;
      }
      
      .exercise-item {
        page-break-inside: avoid;
      }
    }
  </style>
</head>
<body>
  ${exerciseData}
  
  <div class="footer">
    <p>Generated from <span class="brand">Belonging Sessions</span> E-book</p>
    <p>${currentDate}</p>
  </div>
</body>
</html>
  `;

  printWindow.document.write(htmlContent);
  printWindow.document.close();

  // Wait for content to load then trigger print
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.print();
      // Close the window after printing (optional - user can close manually)
      // printWindow.close();
    }, 250);
  };
};

// Generate Page 158 exercise HTML content
export const generatePage158Content = (responses) => {
  const exerciseScenarios = [
    { id: 1, context: "My judgment about someone else when I think they did something wrong.", example: "My partner made an insulting comment about my cooking." },
    { id: 2, context: "My judgment when someone stimulated painful feelings in me.", example: "My best friend told me I should not talk so much when we are with our friends." },
    { id: 3, context: "My judgment when I think someone criticized me.", example: "My boss said I was one of the laziest employees he ever had." },
    { id: 4, context: "My judgment when someone did something I did not want them to do.", example: "My mother did not return my phone call for over a week." },
    { id: 5, context: "My judgment when someone makes a demand of me.", example: "My son told me to stop calling him so much." },
    { id: 6, context: "My judgment when someone diagnoses me.", example: "My husband told me I was a narcissist." },
    { id: 7, context: "My judgment when someone threatens me.", example: "My neighbor told me I had to keep my dog inside the house, or he would call the police." },
  ];

  let exercisesHtml = '';
  
  exerciseScenarios.forEach((scenario) => {
    const response = responses[scenario.id] || {};
    exercisesHtml += `
      <div class="exercise-item">
        <div class="exercise-context">
          <span class="exercise-number">${scenario.id}</span>
          ${scenario.context}
        </div>
        <div class="exercise-example">e.g., "${scenario.example}"</div>
        
        <div class="response-section">
          <div class="response-label judgment">Your Judgment:</div>
          <div class="response-value ${!response.judgment ? 'empty' : ''}">${response.judgment || '(Not filled in)'}</div>
        </div>
        
        <div class="response-section">
          <div class="response-label">Translation:</div>
          <div class="translation-row">
            <span class="translation-prefix">When</span>
            <div class="response-value ${!response.when ? 'empty' : ''}" style="flex: 1;">${response.when || '(Not filled in)'}</div>
          </div>
          <div class="translation-row">
            <span class="translation-prefix response-label feeling">I feel</span>
            <div class="response-value ${!response.feeling ? 'empty' : ''}" style="flex: 1;">${response.feeling || '(Not filled in)'}</div>
          </div>
          <div class="translation-row">
            <span class="translation-prefix response-label need">Because I need</span>
            <div class="response-value ${!response.need ? 'empty' : ''}" style="flex: 1;">${response.need || '(Not filled in)'}</div>
          </div>
        </div>
      </div>
    `;
  });

  return `
    <div class="header">
      <h1>Translating Judgments → Feelings & Needs</h1>
      <div class="subtitle">Page 158 • Belonging Sessions E-book</div>
      <div class="date">My Completed Exercise</div>
    </div>
    
    <div class="wisdom-box">
      "Every judgment is a tragic expression of an unmet need."
      <div class="author">— Marshall Rosenberg</div>
    </div>
    
    <div class="exercise-section">
      <h2>Translation Exercises</h2>
      ${exercisesHtml}
    </div>
    
    ${responses.reflection ? `
    <div class="reflection-section">
      <h2>My Reflection</h2>
      <div class="response-value">${responses.reflection}</div>
    </div>
    ` : ''}
  `;
};

// Generate Chapter 4 exercises HTML content
export const generateChapter4Content = (exercise1Responses, exercise2Data, exercise3Responses) => {
  const exercise1Prompts = [
    { id: 1, prompt: "I love..." },
    { id: 2, prompt: "I value..." },
    { id: 3, prompt: "I need..." },
    { id: 4, prompt: "I long for..." },
    { id: 5, prompt: "I deeply desire..." },
    { id: 6, prompt: "I am happiest when I..." },
    { id: 7, prompt: "I am fulfilled by..." },
    { id: 8, prompt: "I am inspired when..." },
    { id: 9, prompt: "I feel the most alive when..." },
    { id: 10, prompt: "It is important for me to..." },
  ];

  const exercise3Data = [
    { id: 1, statement: "You are such a terrible boss." },
    { id: 2, statement: "I just don't understand why you are so different." },
    { id: 3, statement: "We are late! It's time to go!" },
    { id: 4, statement: "I thought that you liked me." },
    { id: 5, statement: "I can't stand it when you chew with your mouth open." },
    { id: 6, statement: "You are a good daughter/son." },
    { id: 7, statement: "I can do whatever I want. You can't boss me around." },
    { id: 8, statement: "Come on, I know you can do better than that." },
    { id: 9, statement: "You did such a good job cleaning your room." },
    { id: 10, statement: "I wish someone would listen to me." },
    { id: 11, statement: "Whatever..." },
    { id: 12, statement: "We should hang out more often." },
  ];

  // Exercise 1 HTML
  let exercise1Html = '';
  exercise1Prompts.forEach((item) => {
    const response = exercise1Responses[item.id] || {};
    exercise1Html += `
      <div class="exercise-item">
        <div class="exercise-context">
          <span class="exercise-number">${item.id}</span>
          ${item.prompt}
        </div>
        <div class="response-section">
          <div class="response-label">Your Response:</div>
          <div class="response-value ${!response.response ? 'empty' : ''}">${response.response || '(Not filled in)'}</div>
        </div>
        <div class="response-section">
          <div class="response-label need">Need(s) being met:</div>
          <div class="response-value ${!response.needs ? 'empty' : ''}">${response.needs || '(Not filled in)'}</div>
        </div>
      </div>
    `;
  });

  // Exercise 2 HTML
  const selectedNeeds = Object.entries(exercise2Data?.needs || {}).filter(([_, v]) => v).map(([k]) => k);
  const priorityNeeds = Object.entries(exercise2Data?.priority || {}).filter(([_, v]) => v).map(([k]) => k);
  const metNeeds = Object.entries(exercise2Data?.met || {}).filter(([_, v]) => v).map(([k]) => k);
  const unmetNeeds = Object.entries(exercise2Data?.unmet || {}).filter(([_, v]) => v).map(([k]) => k);

  const exercise2Html = `
    <div class="exercise-item">
      <div class="response-section">
        <div class="response-label">Relationship chosen:</div>
        <div class="response-value ${!exercise2Data?.relationship ? 'empty' : ''}">${exercise2Data?.relationship || '(Not specified)'}</div>
      </div>
      <div class="response-section">
        <div class="response-label">Needs selected:</div>
        <div class="response-value ${selectedNeeds.length === 0 ? 'empty' : ''}">${selectedNeeds.length > 0 ? selectedNeeds.join(', ') : '(None selected)'}</div>
      </div>
      <div class="response-section">
        <div class="response-label" style="color: #c9a227;">Priority needs (top 6):</div>
        <div class="response-value ${priorityNeeds.length === 0 ? 'empty' : ''}">${priorityNeeds.length > 0 ? priorityNeeds.join(', ') : '(None marked)'}</div>
      </div>
      <div class="response-section">
        <div class="response-label need">Needs being MET:</div>
        <div class="response-value ${metNeeds.length === 0 ? 'empty' : ''}">${metNeeds.length > 0 ? metNeeds.join(', ') : '(None marked)'}</div>
      </div>
      <div class="response-section">
        <div class="response-label feeling">Needs NOT being met:</div>
        <div class="response-value ${unmetNeeds.length === 0 ? 'empty' : ''}">${unmetNeeds.length > 0 ? unmetNeeds.join(', ') : '(None marked)'}</div>
      </div>
      <div class="response-section">
        <div class="response-label">Summary:</div>
        <div class="response-value">Met: ${metNeeds.length} | Unmet: ${unmetNeeds.length} | Priority: ${priorityNeeds.length}</div>
      </div>
    </div>
  `;

  // Exercise 3 HTML
  let exercise3Html = '';
  exercise3Data.forEach((item) => {
    const response = exercise3Responses[item.id] || {};
    exercise3Html += `
      <div class="exercise-item">
        <div class="exercise-context">
          <span class="exercise-number">${item.id}</span>
          "${item.statement}"
        </div>
        <div class="response-section">
          <div class="translation-row">
            <span class="translation-prefix response-label feeling">I feel</span>
            <div class="response-value ${!response.feeling ? 'empty' : ''}" style="flex: 1;">${response.feeling || '(Not filled in)'}</div>
          </div>
          <div class="translation-row">
            <span class="translation-prefix response-label need">because I need</span>
            <div class="response-value ${!response.need ? 'empty' : ''}" style="flex: 1;">${response.need || '(Not filled in)'}</div>
          </div>
        </div>
      </div>
    `;
  });

  return `
    <div class="header">
      <h1>Chapter 4: Needs</h1>
      <div class="subtitle">Pages 108-113 • Belonging Sessions E-book</div>
      <div class="date">My Completed Exercises</div>
    </div>
    
    <div class="wisdom-box">
      "Needs are the universal aspects of life that contribute to a healthy and full existence for everyone and everything."
    </div>
    
    <div class="exercise-section">
      <h2>Exercise 1: Connecting to Your Needs</h2>
      <p style="margin-bottom: 15px; color: #666; font-size: 14px;">Complete each sentence and identify the needs being met.</p>
      ${exercise1Html}
    </div>
    
    <div class="exercise-section" style="page-break-before: always;">
      <h2>Exercise 2: Relationship Needs</h2>
      <p style="margin-bottom: 15px; color: #666; font-size: 14px;">Exploring needs in a chosen relationship.</p>
      ${exercise2Html}
    </div>
    
    <div class="exercise-section" style="page-break-before: always;">
      <h2>Exercise 3: Translate to Feelings & Needs</h2>
      <p style="margin-bottom: 15px; color: #666; font-size: 14px;">Translate each statement into feelings and needs.</p>
      ${exercise3Html}
    </div>
  `;
};

export default { downloadExercisePDF, generatePage158Content, generateChapter4Content };
